/**
 * Migrated GWP implementation for current theme
 * Integrates with theme's cart system and web components
 */

// Cart API helper functions
const addLineItems = async (data) => {
  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error('Failed to add items to cart');
  }
  
  const result = await response.json();
  
  // Dispatch cart change event for theme components
  const cartResponse = await fetch('/cart.js');
  const cart = await cartResponse.json();
  
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
    bubbles: true,
    detail: { cart, baseEvent: 'gwp:add' }
  }));

  // Force cart drawer refresh if it exists
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { baseEvent: 'gwp:add' }
    }));
  }

  return result;
};

const updateLineItems = async (data) => {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error('Failed to update cart items');
  }
  
  const result = await response.json();
  
  // Dispatch cart change event for theme components
  document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
    bubbles: true,
    detail: { cart: result, baseEvent: 'gwp:update' }
  }));

  // Force cart drawer refresh if it exists
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { baseEvent: 'gwp:update' }
    }));
  }

  return result;
};

// Helper function to trigger comprehensive cart refresh.
// Pass a baseEvent so our own cart:refresh listener can ignore self-triggered refreshes.
const triggerCartRefresh = (baseEvent = 'gwp:refresh') => {
  // Update cart count components
  const cartCounts = document.querySelectorAll('cart-count');
  cartCounts.forEach(count => {
    const event = new CustomEvent('cart:refresh', { detail: { baseEvent } });
    count.dispatchEvent(event);
  });

  // Refresh cart drawer
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { baseEvent }
    }));
  }

  // Update any other cart-related components
  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
    bubbles: true,
    detail: { baseEvent }
  }));
};

// Fetch GWP data from section
const fetchGiftWithPurchaseData = async () => {
  try {
    const response = await fetch('/cart?section_id=gift-with-purchase');
    if (!response.ok) {
      throw new Error('Failed to fetch GWP data');
    }
    
    const data = await response.text();
    const fragment = document.createRange().createContextualFragment(data);
    const scriptElement = fragment.querySelector('[data-gift-with-purchase]');
    
    if (scriptElement) {
      return JSON.parse(scriptElement.innerText);
    }
    
    return { gifts: [], giftsInCart: [] };
  } catch (error) {
    console.error('Error fetching GWP data:', error);
    return { gifts: [], giftsInCart: [] };
  }
};

// Concurrency guard: coalesce overlapping checkGifts calls so two events firing in
// quick succession (e.g. cart:change + cart:refresh while an add is in flight) can't
// both observe pre-add state and issue duplicate /cart/add.js requests for the same
// GWP variant. If a call comes in mid-flight we run a single trailing rerun once the
// in-flight pass settles, so the latest cart state is still picked up.
let gwpInFlight = null;
let gwpPendingRerun = false;

const runCheckGifts = async (giftDetails) => {
  if (!giftDetails) {
    giftDetails = await fetchGiftWithPurchaseData();
  }

  const { gifts, giftsInCart } = giftDetails;
  const qualifyingGifts = gifts.filter((gift) => gift.qualifies);
  const manuallyRemovedGifts = JSON.parse(sessionStorage.getItem('manuallyRemovedGifts')) || [];

  try {
    const invalidGiftsInCart = giftsInCart.filter((giftInCart) => {
      const doesNotQualify = qualifyingGifts.every(
        (qualifyingGift) => qualifyingGift.product.id !== giftInCart.product_id
      );
      const hasMultipleQuantity = giftInCart.quantity > 1;
      return doesNotQualify || hasMultipleQuantity;
    });

    if (invalidGiftsInCart.length) {
      const updates = invalidGiftsInCart.reduce((_updates, _item) => {
        _updates[_item.key] = 0;
        return _updates;
      }, {});
      await updateLineItems({ updates });

      setTimeout(() => {
        triggerCartRefresh('gwp:update');
      }, 100);
      return;
    }

    // giftsInCart is already filtered to _gwp:true lines in Liquid, so the
    // presence of a matching variant_id means we've already auto-added this
    // gift. Don't gate on gwp_discount_applied here: if the discount function
    // hasn't zeroed the line for any reason (config, propagation, threshold
    // edge), we'd otherwise re-add on every page load. A manually-purchased
    // line has no _gwp:true property and never appears in giftsInCart, so it
    // still falls through to auto-add as intended.
    const giftsToAdd = qualifyingGifts.filter((qualifyingGift) => {
      const giftVariantId = qualifyingGift.product.variants[0].id;
      const hasGwpLine = giftsInCart.some(
        (giftInCart) => giftInCart.variant_id === giftVariantId
      );
      const hasNotBeenManuallyRemoved = !manuallyRemovedGifts.includes(qualifyingGift.product.id);
      return !hasGwpLine && hasNotBeenManuallyRemoved;
    });

    if (giftsToAdd.length) {
      const items = giftsToAdd.map(({ product }) => {
        return {
          id: product.variants[0].id,
          quantity: 1,
          properties: {
            _gwp: true,
          },
        };
      });
      await addLineItems({ items });
    }
  } catch (error) {
    console.error('Error processing gifts:', error);
  }
};

export const checkGifts = async (giftDetails) => {
  if (gwpInFlight) {
    gwpPendingRerun = true;
    return gwpInFlight;
  }

  gwpInFlight = (async () => {
    try {
      await runCheckGifts(giftDetails);
    } finally {
      gwpInFlight = null;
    }
  })();

  try {
    return await gwpInFlight;
  } finally {
    if (gwpPendingRerun) {
      gwpPendingRerun = false;
      checkGifts();
    }
  }
};

// Initialize GWP system
export const initializeGWP = () => {
  // Check gifts on page load
  document.addEventListener('DOMContentLoaded', async () => {
    await checkGifts();
  });

  // Check gifts when cart changes
  document.addEventListener('cart:change', async (event) => {
    // Avoid infinite loops by checking the source of the cart change
    const baseEvent = event.detail?.baseEvent;
    if (baseEvent !== 'gwp:add' && baseEvent !== 'gwp:update') {
      await checkGifts();
    }
  });

  // Check gifts when cart is refreshed (skip refreshes we triggered ourselves)
  document.addEventListener('cart:refresh', async (event) => {
    const baseEvent = event.detail?.baseEvent;
    if (baseEvent !== 'gwp:add' && baseEvent !== 'gwp:update' && baseEvent !== 'gwp:refresh') {
      await checkGifts();
    }
  });

  // Handle manual gift removal
  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest('[data-remove-gwp]');
    if (removeButton) {
      const productId = removeButton.getAttribute('data-remove-gwp');
      const manuallyRemovedGifts = JSON.parse(sessionStorage.getItem('manuallyRemovedGifts')) || [];
      
      if (!manuallyRemovedGifts.includes(parseInt(productId))) {
        manuallyRemovedGifts.push(parseInt(productId));
        sessionStorage.setItem('manuallyRemovedGifts', JSON.stringify(manuallyRemovedGifts));
      }
    }
  });

  // Clear manually removed gifts on new session
  window.addEventListener('beforeunload', () => {
    sessionStorage.removeItem('manuallyRemovedGifts');
  });
};

// Auto-initialize if this script is loaded
if (typeof window !== 'undefined') {
  initializeGWP();
}